import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGetOneAssetsTypeQuery } from "../../../../services/supplyChainService";
import Typography from "../../../../components/Typography/Typography";
import RecordForm from "./RecordForm";
import Button from "../../../../components/Button/Button";

const DependencyContainer = ({
    dependency,
    projectId,
    onDependencyChange,
    onRemoveDependency,
    onMissingDependencies,
    index,
    batch,
    allExtractedAssetsDependencies = [],
}) => {
    // State Management
    const [formValues, setFormValues] = useState({
        [`asset_${dependency.asset_type_id}`]: "",
        [`quantity_${dependency.asset_type_id}`]: "",
        [`unit_${dependency.asset_type_id}`]: "",
    });
    const [combinedQuantities, setCombinedQuantities] = useState({});
    const previousHasAssets = useRef(null);

    // API Query
    const {
        data: mergeableAssetsResponse,
        isLoading,
        refetch,
    } = useGetOneAssetsTypeQuery(
        {
            id: projectId,
            assetTypeId: dependency.asset_type_id,
            available_assets: true,
            batch,
        },
        { skip: !projectId || !dependency.asset_type_id },
    );

    // Data Processing
    const filteredAssets =
        mergeableAssetsResponse?.data?.assets
            ?.filter((asset) => asset.qty > 0)
            .map((asset) => ({
                asset_id: asset.asset_id,
                asset_uuid: asset.uuid,
                asset_type_id: asset.asset_type.id,
                qty: asset.qty,
                unit: asset.unit.symbol,
                available: !asset.is_locked,
            })) || [];

    // Helper Functions
    // function uses this data to calculate how much of each asset has been used globally
    const getRemainingQuantities = () => {
        const globalUsedQuantities = {};

        dependency.selectedAssets?.forEach((asset) => {
            const assetId = asset.asset_id;
            if (assetId) {
                globalUsedQuantities[assetId] =
                    (globalUsedQuantities[assetId] || 0) +
                    (combinedQuantities[assetId] || 0);
            }
        });

        // This is used for the extracted assets only
        allExtractedAssetsDependencies?.forEach((otherAssetDeps) => {
            otherAssetDeps.dependencies?.forEach((otherDep) => {
                if (otherDep.asset_type_id === dependency.asset_type_id) {
                    otherDep.selectedAssets?.forEach((selectedAsset) => {
                        const assetId = selectedAsset.asset_id;
                        if (assetId) {
                            globalUsedQuantities[assetId] =
                                (globalUsedQuantities[assetId] || 0) +
                                (selectedAsset.quantity ||
                                    selectedAsset.qty ||
                                    0);
                        }
                    });
                }
            });
        });

        return filteredAssets
            .map((asset) => ({
                ...asset,
                remainingQty:
                    asset.qty - (globalUsedQuantities[asset.asset_id] || 0),
            }))
            .filter((asset) => asset.remainingQty > 0);
    };

    const isFormValid = () => {
        const assetId = formValues[`asset_${dependency.asset_type_id}`];
        const quantity = Number(
            formValues[`quantity_${dependency.asset_type_id}`],
        );
        const unit = formValues[`unit_${dependency.asset_type_id}`];

        return Boolean(assetId) && quantity > 0 && Boolean(unit);
    };

    // Effects
    useEffect(() => {
        if (!isLoading && mergeableAssetsResponse?.data?.assets) {
            const hasAvailableAssets = mergeableAssetsResponse.data.assets.some(
                (asset) => asset.qty > 0,
            );

            if (previousHasAssets.current !== hasAvailableAssets) {
                previousHasAssets.current = hasAvailableAssets;
                onMissingDependencies?.(!hasAvailableAssets);
            }
        }
    }, [isLoading, mergeableAssetsResponse]);
    
    useEffect(() => {
        if (dependency.selectedAssets && dependency.selectedAssets.length > 0) {
            const initialQuantities = {};
            dependency.selectedAssets.forEach((asset) => {
                const assetId = asset.asset_id || asset.assetId;
                const quantity = asset.quantity || asset.qty;
                if (assetId && quantity) {
                    initialQuantities[assetId] = quantity;
                }
            });
            setCombinedQuantities(initialQuantities);
        } else {
            setCombinedQuantities({});
        }
    }, [dependency.selectedAssets]);

    // Event Handlers
    const handleInputChange = (field) => (e) => {
        const value = e.target.value;
        if (field === `asset_${dependency.asset_type_id}`) {
            const selectedAsset = filteredAssets.find(
                (asset) => asset.asset_id === value,
            );
            setFormValues((prev) => ({
                ...prev,
                [field]: value,
                [`unit_${dependency.asset_type_id}`]: selectedAsset?.unit || "",
            }));
            return;
        }
        setFormValues((prev) => ({ ...prev, [field]: value }));
    };

    const handleAdd = () => {
        const asset_id = formValues[`asset_${dependency.asset_type_id}`];
        const quantity = Number(
            formValues[`quantity_${dependency.asset_type_id}`],
        );
        const unit = formValues[`unit_${dependency.asset_type_id}`];

        if (!asset_id || !quantity || !unit) return;

        const selectedAsset = filteredAssets.find(
            (asset) => asset.asset_id === asset_id,
        );

        const existingQuantity = combinedQuantities[asset_id] || 0;
        const totalQuantity = existingQuantity + quantity;

        const remainingAsset = getRemainingQuantities().find(
            (asset) => asset.asset_id === asset_id,
        );

        if (!remainingAsset) {
            toast.error(
                `Asset ${asset_id} is no longer available or has been fully allocated to other assets`,
            );
            return;
        }

        if (quantity > remainingAsset.remainingQty) {
            toast.error(
                `Quantity cannot exceed remaining amount of ${remainingAsset.remainingQty} ${remainingAsset.unit}. This asset may be partially used by other extracted assets.`,
            );
            return;
        }

        if (totalQuantity > selectedAsset.qty) {
            toast.error(
                `Total quantity cannot exceed ${selectedAsset.qty} ${unit}`,
            );
            return;
        }

        // Update combined quantities by adding the new quantity
        setCombinedQuantities((prev) => ({
            ...prev,
            [asset_id]: totalQuantity,
        }));

        const existingAssetIndex = dependency.selectedAssets?.findIndex(
            (asset) => asset.asset_id === asset_id,
        );

        if (existingAssetIndex !== -1) {
            // Update existing asset quantity
            const updatedSelectedAssets = [...dependency.selectedAssets];
            updatedSelectedAssets[existingAssetIndex].quantity = totalQuantity;

            onDependencyChange({
                ...dependency,
                selectedAssets: updatedSelectedAssets,
            });
        } else {
            // Add new asset
            const newAsset = {
                asset_id,
                asset_uuid: selectedAsset?.asset_uuid,
                quantity: totalQuantity,
                unit,
            };

            onDependencyChange({
                ...dependency,
                selectedAssets: [
                    ...(dependency.selectedAssets || []),
                    newAsset,
                ],
            });
        }

        setFormValues({
            [`asset_${dependency.asset_type_id}`]: "",
            [`quantity_${dependency.asset_type_id}`]: "",
            [`unit_${dependency.asset_type_id}`]: "",
        });
    };

    // Render Helpers
    const renderBadges = () => (
        <div className="selected-dependencies mt-1">
            {Object.entries(combinedQuantities).map(([asset_id, totalQty]) => {
                const asset = filteredAssets.find(
                    (a) => a.asset_id === asset_id,
                );
                if (!asset) return null;

                return (
                    <span
                        key={asset_id}
                        className="badge me-2 mb-2 p-2 d-inline-flex align-items-center"
                        style={{
                            fontSize: "0.9rem",
                            backgroundColor: "#DBEAFE",
                            color: "#1C8AD1",
                            border: "1px solid #DBEAFE",
                        }}
                    >
                        {asset_id} - {totalQty} {asset.unit}
                        <i
                            className="bi bi-x-circle ms-2"
                            style={{
                                cursor: "pointer",
                                color: "#1C8AD1",
                            }}
                            onClick={() => {
                                const newQuantities = { ...combinedQuantities };
                                delete newQuantities[asset_id];
                                setCombinedQuantities(newQuantities);

                                const newSelectedAssets =
                                    dependency.selectedAssets.filter(
                                        (asset) => asset.asset_id !== asset_id,
                                    );

                                onDependencyChange({
                                    ...dependency,
                                    selectedAssets: newSelectedAssets,
                                });
                            }}
                        />
                    </span>
                );
            })}
        </div>
    );

    // Loading State
    if (isLoading) {
        return <div>Loading mergeable assets...</div>;
    }

    // Empty State
    if (!mergeableAssetsResponse?.data?.assets?.length) {
        return (
            <div className="dependency-item mb-4 bg-white">
                <Typography variant="h3" className="mb-4 fw-bold">
                    Type {(index + 1).toString().padStart(2, "0")} -{" "}
                    {dependency.asset_value}
                </Typography>
                <div>
                    <Typography variant="body1">No assets found.</Typography>
                </div>
            </div>
        );
    }

    // Main Render
    return (
        <div className="dependency-item  mb-4 bg-white">
            <Typography variant="h3" className="fw-bold">
                Type {(index + 1).toString().padStart(2, "0")} -{" "}
                {dependency.label}
            </Typography>

            {/* Show warning if assets are limited due to global usage */}
            {allExtractedAssetsDependencies.length > 0 &&
                filteredAssets.length > getRemainingQuantities().length && (
                    <div
                        className="alert alert-info mt-2 mb-3"
                        style={{ fontSize: "0.9rem" }}
                    >
                        <i className="bi bi-info-circle me-2"></i>
                        Some assets are not available because they are allocated
                        to other extracted assets. Only{" "}
                        {getRemainingQuantities().length} of{" "}
                        {filteredAssets.length} assets are available for
                        selection.
                    </div>
                )}

            <div className="row g-3 align-items-end">
                <div className="col-5">
                    <RecordForm
                        fieldType="dropdown"
                        name={`asset_${dependency.asset_type_id}`}
                        label="Asset"
                        value={formValues[`asset_${dependency.asset_type_id}`]}
                        onChange={handleInputChange(
                            `asset_${dependency.asset_type_id}`,
                        )}
                        options={getRemainingQuantities().map((asset) => ({
                            label: `${asset.asset_id} (${asset.remainingQty} ${asset.unit})`,
                            value: asset.asset_id,
                            uuid: asset.asset_uuid,
                        }))}
                    />
                </div>
                <div className="col-3">
                    <RecordForm
                        fieldType="number"
                        name={`quantity_${dependency.asset_type_id}`}
                        label="Quantity"
                        value={
                            formValues[`quantity_${dependency.asset_type_id}`]
                        }
                        onChange={handleInputChange(
                            `quantity_${dependency.asset_type_id}`,
                        )}
                        min="0"
                        step="1"
                    />
                </div>
                {formValues[`unit_${dependency.asset_type_id}`] && (
                    <div className="col-2 my-4">
                        <label className="form-label">Unit</label>
                        <div className="form-control bg-light">
                            {formValues[`unit_${dependency.asset_type_id}`]}
                        </div>
                    </div>
                )}
                <div className="col-2 my-4">
                    <Button
                        variant="primary"
                        onClick={handleAdd}
                        disabled={!isFormValid()}
                        className="w-100"
                    >
                        Add
                    </Button>
                </div>
            </div>
            {renderBadges()}
        </div>
    );
};

export default DependencyContainer;
